import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import type {
  Account,
  AccountType,
  InvestmentSummary,
  BrokerageSummary,
  InvestmentProductValuation,
  InvestmentAssetCategory,
  Member,
} from '@zeni/shared'
import { INVESTMENT_ASSET_CATEGORIES, quantityUnitOf } from '@zeni/shared'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useAmountFormat } from '../contexts/AmountUnitContext'
import { plusSign, withoutZeroSign } from '../lib/amount-format'
import type { TranslationKey } from '../i18n/translations'
import { accountsApi, investmentsApi, membersApi } from '../lib/api'
import { useModalReset } from '../lib/use-modal-reset'
import { InstitutionCombobox, type Institution } from '../components/InstitutionCombobox'
import { MemberCombobox } from '../components/MemberCombobox'
import { AccountModal, AccountTile } from '../components/AccountShared'
import { btnDanger, btnGhost, btnLinkDanger, btnLinkMuted, btnPrimary, btnSecondary, cardGrid, modalTitle, pageSubtitle, pageTitle, sectionTitle, subheading } from '../lib/ui'

/** 証券会社が紐づいていない証券口座を編集するときに使う種別（種別の選び直しはさせない） */
const SECURITIES_ONLY_TYPES: AccountType[] = ['investment']

/** 所有者が未設定の証券口座をまとめるグループのキー。メンバーIDと衝突しない値にする */
const NO_MEMBER_KEY = '__no_member__'

/** 証券口座のカラー既定値。口座ページのカードと同じインディゴ */
const DEFAULT_BROKERAGE_COLOR = '#6366f1'

// 預り区分の内訳バーの色。ダッシュボードの資産構成と同じトーンで並べる
const BREAKDOWN_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#0ea5e9', '#94a3b8'] as const

/** 内訳に個別で出す件数。これを超えた区分は 1 行にまとめる */
const BREAKDOWN_MAX_ROWS = 4

/** まとめた区分に使うキー。実在の預り区分名と衝突しない値にする */
const BREAKDOWN_REST_KEY = '__rest__'

const quantityFormat = new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 4 })
// 基準価額は 1 万円前後の値で、万円表示にすると桁が潰れて読めない。ここは常に円で出す
const priceFormat = new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', maximumFractionDigits: 0 })

/** 未取得の値は '—' で埋める。金額の表示単位は設定に従う */
function useCurrencyFormat() {
  const { formatJPY } = useAmountFormat()
  return useCallback((value: number | null) => (value === null ? '—' : formatJPY(value)), [formatJPY])
}

/** 自動更新の最終実行時刻。日付と時刻が分かれば十分なので分までにする */
function formatDateTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat('ja-JP', { dateStyle: 'short', timeStyle: 'short' }).format(d)
}

/** 前日比の色。増えたら緑、減ったら赤、変わらなければグレー */
function changeToneClass(value: number): string {
  if (value > 0) return 'text-emerald-600 dark:text-emerald-400'
  if (value < 0) return 'text-red-600 dark:text-red-400'
  return 'text-gray-400 dark:text-gray-500'
}

/**
 * 前日比の表示。色だけだと増減が伝わらない見え方（モノクロ・色覚特性）もあるため、
 * ▲▼ の記号も添える。前日の基準価額が取れていない場合は何も出さない。
 */
function DayChange({ value, format, className = '' }: {
  value: number | null
  format: (v: number) => string
  className?: string
}) {
  const { t } = useLanguage()
  if (value === null) return null
  const mark = value > 0 ? '▲' : value < 0 ? '▼' : '±'
  return (
    <span className={`${changeToneClass(value)} ${className}`} title={t('inv_day_change')}>
      {mark}{format(Math.abs(value))}
    </span>
  )
}

// カテゴリー ID から翻訳キーを組み立てる。TranslationKey に無いキーは
// 型エラーになるので、翻訳の追加漏れはビルド時に気づける。
function categoryKey(category: InvestmentAssetCategory): TranslationKey {
  return `inv_category_${category}` as TranslationKey
}

// ─────────────────────────────────────────────
// 証券会社の追加・編集モーダル
// ─────────────────────────────────────────────

function BrokerageModal({
  open, initial, members, token, onClose, onSaved,
}: {
  open: boolean
  initial: BrokerageSummary | null
  /** 所有者の候補。ページ側で取得済みのものを渡す */
  members: Member[]
  token: string
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useLanguage()
  const [name, setName] = useState('')
  const [institution, setInstitution] = useState('')
  const [memberId, setMemberId] = useState('')
  const [color, setColor] = useState(DEFAULT_BROKERAGE_COLOR)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // ユーザーが自分でカラーを触ったか。触っていない間だけ、選んだ証券会社の
  // コーポレートカラーで塗り替える（口座ページの編集モーダルと同じ振る舞い）
  const colorTouchedRef = useRef(false)

  useModalReset(open, initial?.id ?? null, () => {
    setName(initial?.name ?? '')
    setInstitution(initial?.institution ?? '')
    setMemberId(initial?.memberId ?? '')
    setColor(initial?.color ?? DEFAULT_BROKERAGE_COLOR)
    colorTouchedRef.current = false
    setError(null)
  })

  if (!open) return null

  // 証券会社を選んだ時点で口座名が空なら、そのまま口座名として使う。
  // 1 証券会社 1 口座で足りるユーザーは口座名を入力せずに登録できる。
  const handleInstitutionChange = (value: string, picked?: Institution) => {
    setInstitution(value)
    if (name.trim() === '' || name === institution) setName(value)
    if (!colorTouchedRef.current && picked?.brandColor) setColor(picked.brandColor)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // InstitutionCombobox は input の required を持たないため、ここで空を弾く
    if (name.trim() === '' || institution.trim() === '') return
    setSaving(true)
    setError(null)
    try {
      const base = { name: name.trim(), institution: institution.trim(), color: color || null }
      if (initial) {
        // 更新では null を送って所有者の割り当てを外せる
        await investmentsApi.updateBrokerage(token, initial.id, {
          ...base,
          memberId: memberId === '' ? null : memberId,
        })
      } else {
        await investmentsApi.createBrokerage(token, {
          ...base,
          color: color || undefined,
          memberId: memberId || undefined,
        })
      }
      onSaved()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5 dark:bg-gray-900">
        <h2 className={`${modalTitle} mb-4`}>
          {t(initial ? 'inv_brokerage_modal_edit' : 'inv_brokerage_modal_add')}
        </h2>
        <form onSubmit={(e) => { void handleSubmit(e) }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              {t('inv_brokerage_institution')} <span className="text-red-500">*</span>
            </label>
            {/* 証券会社名は金融機関マスタの証券会社カテゴリから選べるようにする（自由入力も可） */}
            <InstitutionCombobox
              value={institution}
              onChange={handleInstitutionChange}
              token={token}
              placeholder="楽天証券"
              filterCategory="securities"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              {t('inv_brokerage_name')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="楽天証券 つみたて用"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('inv_brokerage_name_hint')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('accounts_form_member')}</label>
            <MemberCombobox
              value={memberId}
              members={members}
              onChange={setMemberId}
              placeholder={t('accounts_form_member_none')}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
            {members.length === 0 && (
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{t('accounts_form_member_hint')}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('accounts_form_color')}</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={color}
                onChange={(e) => { colorTouchedRef.current = true; setColor(e.target.value) }}
                className="h-9 w-16 rounded border border-gray-200 cursor-pointer dark:border-gray-600"
              />
              <input
                type="text"
                value={color}
                onChange={(e) => { colorTouchedRef.current = true; setColor(e.target.value) }}
                placeholder={DEFAULT_BROKERAGE_COLOR}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className={btnGhost}>
              {t('common_cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className={btnPrimary}
            >
              {t('common_save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// 商品の追加・編集モーダル
// ─────────────────────────────────────────────

function ProductModal({
  open, brokerageId, initial, accountTypeOptions, token, onClose, onSaved,
}: {
  open: boolean
  brokerageId: string
  initial: InvestmentProductValuation | null
  /** 入力補助に使う、登録済みの預り区分一覧 */
  accountTypeOptions: string[]
  token: string
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useLanguage()
  const [name, setName] = useState('')
  const [category, setCategory] = useState<InvestmentAssetCategory>('equity_fund')
  const [accountType, setAccountType] = useState('')
  const [quantity, setQuantity] = useState('0')
  const [fundCd, setFundCd] = useState('')
  const [cost, setCost] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useModalReset(open, initial?.id ?? null, () => {
    setName(initial?.name ?? '')
    setCategory(initial?.category ?? 'equity_fund')
    setAccountType(initial?.accountType ?? '')
    setQuantity(String(initial?.quantity ?? 0))
    setFundCd(initial?.associationFundCd ?? '')
    setCost(initial?.acquisitionCost === null || initial?.acquisitionCost === undefined ? '' : String(initial.acquisitionCost))
    setError(null)
  })

  if (!open) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const payload = {
        category,
        name,
        accountType: accountType.trim(),
        associationFundCd: fundCd.trim() === '' ? null : fundCd.trim(),
        quantity: Number(quantity),
        acquisitionCost: cost.trim() === '' ? null : Number(cost),
      }
      if (initial) await investmentsApi.updateProduct(token, initial.id, payload)
      else await investmentsApi.createProduct(token, { brokerageId, ...payload })
      onSaved()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  const inputClass = 'border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5 dark:bg-gray-900">
        <h2 className={`${modalTitle} mb-4`}>
          {t(initial ? 'inv_product_modal_edit' : 'inv_product_modal_add')}
        </h2>
        <form onSubmit={(e) => { void handleSubmit(e) }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              {t('inv_product_name')} <span className="text-red-500">*</span>
            </label>
            <input type="text" required value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              {t('inv_product_category')}
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as InvestmentAssetCategory)}
              className={inputClass}
            >
              {INVESTMENT_ASSET_CATEGORIES.map((c) => (
                <option key={c} value={c}>{t(categoryKey(c))}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              {t('inv_product_account_type')}
            </label>
            {/* 預り区分は証券会社ごとに呼び名が違うため自由入力にし、既出の区分だけ候補に出す */}
            <input
              type="text"
              list="inv-account-type-options"
              value={accountType}
              onChange={(e) => setAccountType(e.target.value)}
              placeholder="NISAつみたて投資枠"
              className={inputClass}
            />
            <datalist id="inv-account-type-options">
              {accountTypeOptions.map((opt) => <option key={opt} value={opt} />)}
            </datalist>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('inv_product_account_type_hint')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              {t('inv_product_quantity')}（{quantityUnitOf(category)}）
            </label>
            <input type="number" step="any" min="0" required value={quantity} onChange={(e) => setQuantity(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              {t('inv_product_fund_cd')}
            </label>
            <input type="text" value={fundCd} onChange={(e) => setFundCd(e.target.value)} placeholder="03311187" className={inputClass} />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('inv_product_fund_cd_hint')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              {t('inv_product_cost')}
            </label>
            <input type="number" step="any" value={cost} onChange={(e) => setCost(e.target.value)} className={inputClass} />
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className={btnGhost}>
              {t('common_cancel')}
            </button>
            <button type="submit" disabled={saving} className={btnPrimary}>
              {t('common_save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// 証券会社カード（カテゴリー > 商品のテーブル）
// ─────────────────────────────────────────────

function BrokerageCard({
  brokerage, token, onChanged, onEditBrokerage, onAddProduct, onEditProduct, onError, onNotice,
}: {
  brokerage: BrokerageSummary
  token: string
  onChanged: () => void
  onEditBrokerage: (b: BrokerageSummary) => void
  onAddProduct: (brokerageId: string) => void
  onEditProduct: (p: InvestmentProductValuation) => void
  onError: (message: string) => void
  onNotice: (message: string) => void
}) {
  const { t } = useLanguage()
  const formatCurrency = useCurrencyFormat()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [importing, setImporting] = useState(false)

  // 評価損益は商品ごとに端数処理した値の合計。評価額 - 取得金額で出すと 1 円ずれる
  const gainLoss = brokerage.gainLoss

  const handleFile = async (file: File) => {
    setImporting(true)
    onError('')
    try {
      const result = await investmentsApi.importCsv(token, brokerage.id, file)
      const summary = t('inv_import_result')
        .replace('{imported}', String(result.imported))
        .replace('{skipped}', String(result.skipped))
      onNotice(summary)
      onChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err))
    } finally {
      setImporting(false)
      // 同じファイルを続けて選び直せるよう値をクリアする
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleDeleteBrokerage = async () => {
    if (!confirm(t('inv_brokerage_delete_confirm').replace('{name}', brokerage.name))) return
    try {
      await investmentsApi.deleteBrokerage(token, brokerage.id)
      onChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err))
    }
  }

  const handleDeleteProduct = async (product: InvestmentProductValuation) => {
    // 同名の商品が預り区分ごとに並ぶので、どれを消すのか分かる名前で確認する
    const label = product.accountType === '' ? product.name : `${product.name}（${product.accountType}）`
    if (!confirm(t('inv_product_delete_confirm').replace('{name}', label))) return
    try {
      await investmentsApi.deleteProduct(token, product.id)
      onChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <section
      className="overflow-hidden rounded-xl border border-l-4 border-gray-200 bg-white dark:bg-gray-900 dark:border-gray-700"
      // 口座に設定したカラーを縁に出して、口座ページのカードと同じ色で見分けられるようにする
      style={{ borderLeftColor: brokerage.color ?? DEFAULT_BROKERAGE_COLOR }}
    >
      <header className="flex flex-col gap-3 p-4 border-b border-gray-200 sm:flex-row sm:items-center sm:justify-between dark:border-gray-700">
        <div>
          {/* 口座名から、この証券口座で絞り込んだ取引一覧へ飛べるようにする */}
          <h2 className={sectionTitle}>
            <Link
              to={`/transactions?accountId=${brokerage.accountId}`}
              className="hover:underline"
              title={t('accounts_card_view_transactions')}
            >
              {brokerage.name}
            </Link>
          </h2>
          {/* 口座名を証券会社名と別に付けている場合だけ、どの証券会社かを添える */}
          {brokerage.institution !== '' && brokerage.institution !== brokerage.name && (
            <p className="text-xs text-gray-400 dark:text-gray-500">{brokerage.institution}</p>
          )}
          {brokerage.acquisitionCost > 0 && (
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              {t('inv_total_cost_sum')}:{' '}
              <span className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(brokerage.acquisitionCost)}</span>
            </p>
          )}
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            {t('inv_total_market_value')}:{' '}
            <span className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(brokerage.marketValue)}</span>
            {brokerage.acquisitionCost > 0 && (
              <span className={gainLoss >= 0 ? 'ml-2 text-emerald-600 dark:text-emerald-400' : 'ml-2 text-red-600 dark:text-red-400'}>
                {plusSign(gainLoss)}{formatCurrency(gainLoss)}
              </span>
            )}
            {brokerage.marketValueChange !== null && (
              <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                {t('inv_day_change')}{' '}
                <DayChange value={brokerage.marketValueChange} format={(v) => formatCurrency(v)} />
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f) }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className={btnSecondary}
          >
            {importing ? t('inv_importing') : t('inv_import_csv')}
          </button>
          <button
            onClick={() => onAddProduct(brokerage.id)}
            className={btnSecondary}
          >
            {t('inv_add_product')}
          </button>
          <button
            onClick={() => onEditBrokerage(brokerage)}
            className={btnSecondary}
          >
            {t('categories_edit')}
          </button>
          <button
            onClick={() => { void handleDeleteBrokerage() }}
            className={btnDanger}
          >
            {t('categories_delete')}
          </button>
        </div>
      </header>

      {brokerage.categories.length === 0 ? (
        <p className="p-4 text-center text-sm text-gray-400 dark:text-gray-500">{t('inv_no_products')}</p>
      ) : (
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {brokerage.categories.map((group) => (
            <div key={group.category} className="p-4">
              <div className="flex items-baseline justify-between mb-2">
                <h3 className={subheading}>{t(categoryKey(group.category))}</h3>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {formatCurrency(group.marketValue)}
                  <DayChange value={group.marketValueChange} format={(v) => formatCurrency(v)} className="ml-2 text-xs" />
                </span>
              </div>
              {/* 幅の広い数値テーブルなので、狭い画面ではテーブル自身を横スクロールさせる */}
              <div className="overflow-x-auto">
                <table className="w-full min-w-[840px] text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                      <th className="py-2 pr-3 font-medium">{t('inv_col_product')}</th>
                      <th className="py-2 px-3 font-medium">{t('inv_col_account_type')}</th>
                      <th className="py-2 px-3 font-medium text-right">{t('inv_col_quantity')}</th>
                      <th className="py-2 px-3 font-medium text-right">{t('inv_col_price')}</th>
                      <th className="py-2 px-3 font-medium text-right">{t('inv_col_market_value')}</th>
                      <th className="py-2 px-3 font-medium text-right">{t('inv_col_cost')}</th>
                      <th className="py-2 px-3 font-medium text-right">{t('inv_col_gain_loss')}</th>
                      <th className="py-2 pl-3" />
                    </tr>
                  </thead>
                  {/* 同じ商品でも預り区分ごとに別の保有として並ぶため、区分単位で小計を挟む */}
                  {group.accountTypes.map((accountGroup) => (
                    <tbody key={accountGroup.accountType} className="divide-y divide-gray-100 dark:divide-gray-800">
                      {group.accountTypes.length > 1 && (
                        <tr className="bg-gray-50 dark:bg-gray-800/60">
                          <td colSpan={4} className="py-1.5 pr-3 text-xs font-medium text-gray-600 dark:text-gray-300">
                            {accountGroup.accountType === '' ? t('inv_account_type_none') : accountGroup.accountType}
                          </td>
                          <td className="py-1.5 px-3 text-right text-xs font-medium tabular-nums text-gray-700 dark:text-gray-200">
                            {formatCurrency(accountGroup.marketValue)}
                            <DayChange
                              value={accountGroup.marketValueChange}
                              format={(v) => formatCurrency(v)}
                              className="ml-2 font-normal"
                            />
                          </td>
                          <td className="py-1.5 px-3 text-right text-xs tabular-nums text-gray-500 dark:text-gray-400">
                            {formatCurrency(accountGroup.acquisitionCost)}
                          </td>
                          <td className="py-1.5 px-3 text-right text-xs tabular-nums">
                            <span className={accountGroup.gainLoss >= 0
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : 'text-red-600 dark:text-red-400'}>
                              {plusSign(accountGroup.gainLoss)}
                              {formatCurrency(accountGroup.gainLoss)}
                            </span>
                          </td>
                          <td className="py-1.5 pl-3" />
                        </tr>
                      )}
                      {accountGroup.products.map((product) => (
                        <tr key={product.id}>
                          <td className="py-2 pr-3 text-gray-900 dark:text-gray-100">
                            {product.name}
                            {product.associationFundCd && (
                              <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">{product.associationFundCd}</span>
                            )}
                          </td>
                          <td className="py-2 px-3 whitespace-nowrap">
                            {product.accountType === '' ? (
                              <span className="text-xs text-gray-400 dark:text-gray-500">{t('inv_account_type_none')}</span>
                            ) : (
                              <span className="inline-block rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">
                                {product.accountType}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-right tabular-nums text-gray-700 dark:text-gray-300">
                            {quantityFormat.format(product.quantity)}
                            <span className="ml-1 text-xs text-gray-400">{quantityUnitOf(product.category)}</span>
                          </td>
                          <td className="py-2 px-3 text-right tabular-nums text-gray-700 dark:text-gray-300">
                            {product.price === null ? (
                              <span className="text-amber-600 dark:text-amber-400">{t('inv_price_unavailable')}</span>
                            ) : (
                              <>
                                {priceFormat.format(product.price)}
                                {product.priceChange !== null && (
                                  <span className="block text-xs">
                                    <DayChange value={product.priceChange} format={(v) => priceFormat.format(v)} />
                                    {product.priceChangeRate !== null && (
                                      <span className={`ml-1 ${changeToneClass(product.priceChange)}`}>
                                        ({plusSign(product.priceChangeRate, 2)}{withoutZeroSign(product.priceChangeRate, 2).toFixed(2)}%)
                                      </span>
                                    )}
                                  </span>
                                )}
                                {product.priceDate && (
                                  <span className="block text-xs text-gray-400 dark:text-gray-500">
                                    {t('inv_price_as_of').replace('{date}', product.priceDate)}
                                  </span>
                                )}
                              </>
                            )}
                          </td>
                          <td className="py-2 px-3 text-right tabular-nums font-medium text-gray-900 dark:text-gray-100">
                            {formatCurrency(product.marketValue)}
                            {product.marketValueChange !== null && (
                              <DayChange
                                value={product.marketValueChange}
                                format={(v) => formatCurrency(v)}
                                className="block text-xs font-normal"
                              />
                            )}
                          </td>
                          <td className="py-2 px-3 text-right tabular-nums text-gray-500 dark:text-gray-400">
                            {formatCurrency(product.acquisitionCost)}
                          </td>
                          <td className="py-2 px-3 text-right tabular-nums">
                            {product.gainLoss === null ? (
                              <span className="text-gray-400">—</span>
                            ) : (
                              <span className={product.gainLoss >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                                {plusSign(product.gainLoss)}{formatCurrency(product.gainLoss)}
                                {product.gainLossRate !== null && (
                                  <span className="block text-xs">
                                    {plusSign(product.gainLossRate, 2)}{withoutZeroSign(product.gainLossRate, 2).toFixed(2)}%
                                  </span>
                                )}
                              </span>
                            )}
                          </td>
                          <td className="py-2 pl-3 text-right whitespace-nowrap">
                            <button
                              onClick={() => onEditProduct(product)}
                              className={btnLinkMuted}
                            >
                              {t('categories_edit')}
                            </button>
                            <button
                              onClick={() => { void handleDeleteProduct(product) }}
                              className={`${btnLinkDanger} ml-2`}
                            >
                              {t('categories_delete')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  ))}
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

// ─────────────────────────────────────────────
// ページ本体
// ─────────────────────────────────────────────

export default function Investments() {
  const { accessToken } = useAuth()
  const { t } = useLanguage()
  const formatCurrency = useCurrencyFormat()
  const [summary, setSummary] = useState<InvestmentSummary | null>(null)
  const [members, setMembers] = useState<Member[]>([])
  const [investmentAccounts, setInvestmentAccounts] = useState<Account[]>([])
  const [unlinkedAccount, setUnlinkedAccount] = useState<Account | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [brokerageModal, setBrokerageModal] = useState<{ open: boolean; initial: BrokerageSummary | null }>({ open: false, initial: null })
  const [productModal, setProductModal] = useState<{ open: boolean; brokerageId: string; initial: InvestmentProductValuation | null }>(
    { open: false, brokerageId: '', initial: null },
  )

  const fetchSummary = useCallback(() => {
    if (!accessToken) return
    setLoading(true)
    investmentsApi.summary(accessToken)
      .then((res) => setSummary(res.data))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : t('common_fetch_error')))
      .finally(() => setLoading(false))
    // 証券会社が紐づいていない証券口座（外部連携が作ったものなど）も拾うために口座も見る。
    // ここで拾わないと、口座ページから証券口座を外した結果どこにも出なくなってしまう
    accountsApi.list(accessToken, { includeArchived: true })
      .then((res) => setInvestmentAccounts(res.data.filter((a) => a.type === 'investment')))
      .catch(() => undefined)
  }, [accessToken, t])

  useEffect(() => { fetchSummary() }, [fetchSummary])

  // 証券口座を所有者ごとに並べるために使う。取得に失敗しても一覧は表示する
  useEffect(() => {
    if (!accessToken) return
    void membersApi.list(accessToken).then((res) => setMembers(res.data)).catch(() => undefined)
  }, [accessToken])

  const gainLoss = summary ? summary.gainLoss : 0
  // 評価損益率。取得金額が分からない（0）と割れないので出さない
  const gainLossRate = summary && summary.acquisitionCost > 0
    ? (summary.gainLoss / summary.acquisitionCost) * 100
    : null

  // 預り区分ごとの評価額。証券会社を跨いで合算し、多い順に並べる。
  // 5 件目からは 1 つにまとめて、凡例が伸びて他のカードより高くならないようにする
  const accountTypeBreakdown = (() => {
    const total = summary?.marketValue ?? 0
    if (total <= 0) return []
    const byType = new Map<string, number>()
    for (const b of summary?.brokerages ?? []) {
      for (const c of b.categories) {
        for (const g of c.accountTypes) {
          byType.set(g.accountType, (byType.get(g.accountType) ?? 0) + g.marketValue)
        }
      }
    }
    const rows = [...byType.entries()]
      .filter(([, marketValue]) => marketValue > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([accountType, marketValue]) => ({
        accountType,
        label: accountType === '' ? t('inv_account_type_none') : accountType,
        marketValue,
      }))
    const head = rows.slice(0, BREAKDOWN_MAX_ROWS)
    const restValue = rows.slice(BREAKDOWN_MAX_ROWS).reduce((sum, r) => sum + r.marketValue, 0)
    const merged = restValue > 0
      ? [...head, { accountType: BREAKDOWN_REST_KEY, label: t('inv_account_type_breakdown_rest'), marketValue: restValue }]
      : head
    return merged.map((r) => ({ ...r, share: (r.marketValue / total) * 100 }))
  })()

  // 商品モーダルの預り区分入力の候補。登録済みの区分をそのまま使い回せるようにする。
  // iDeCo だけは1件も登録していないうちから候補に出す（証券口座と違って呼び名が
  // 証券会社によらず一定で、iDeCo の取引履歴CSVインポートが付ける区分でもあるため）。
  const accountTypeOptions = [...new Set([
    ...(summary?.brokerages ?? [])
      .flatMap((b) => b.categories)
      .flatMap((c) => c.accountTypes)
      .map((g) => g.accountType)
      .filter((v) => v !== ''),
    'iDeCo',
  ])].sort((a, b) => a.localeCompare(b, 'ja'))

  // 口座ページと同じく所有者ごとにまとめる。メンバーの表示順どおりに並べ、
  // 所有者未設定（と、既に消えたメンバーを指す口座）は最後
  const brokerages = summary?.brokerages ?? []
  const memberIds = new Set(members.map((m) => m.id))
  const groups = [
    ...members.map((m) => ({
      key: m.id,
      name: m.name,
      brokerages: brokerages.filter((b) => b.memberId === m.id),
    })),
    {
      key: NO_MEMBER_KEY,
      name: t('accounts_member_none_group'),
      brokerages: brokerages.filter((b) => !b.memberId || !memberIds.has(b.memberId)),
    },
  ].filter((g) => g.brokerages.length > 0)

  // 証券会社の行を持たない証券口座。通常は発生しないが、外部連携や過去のデータで
  // 残っているものが行方不明にならないよう、口座カードとして別枠で出す
  const linkedAccountIds = new Set(brokerages.map((b) => b.accountId))
  const unlinkedAccounts = investmentAccounts.filter((a) => !linkedAccountIds.has(a.id))

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className={pageTitle}>{t('inv_title')}</h1>
          <p className={pageSubtitle}>{t('inv_subtitle')}</p>
          {/* 毎日 cron が取り込むので、手動操作なしで最新であることを明示する */}
          <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
            {summary?.lastPriceUpdatedAt
              ? t('inv_price_auto_updated').replace('{datetime}', formatDateTime(summary.lastPriceUpdatedAt))
              : t('inv_price_auto_updated_never')}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setBrokerageModal({ open: true, initial: null })}
            className={btnPrimary}
          >
            {t('inv_add_brokerage')}
          </button>
        </div>
      </div>

      {summary && summary.brokerages.length > 0 && (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="bg-white rounded-xl border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('inv_total_market_value')}</p>
            <p className="mt-1 text-xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(summary.marketValue)}</p>
            {summary.marketValueChange !== null && (
              <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                {t('inv_day_change')}{' '}
                <DayChange value={summary.marketValueChange} format={(v) => formatCurrency(v)} className="font-medium" />
              </p>
            )}
          </div>
          {/* 取得金額と評価損益は「元手といくら増えたか」で一組なので同じカードに並べる */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('inv_total_cost')}</p>
            <p className="mt-1 text-xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(summary.acquisitionCost)}</p>
            <div className="mt-2 flex items-baseline justify-between gap-2 border-t border-gray-100 pt-2 dark:border-gray-800">
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('inv_total_gain_loss')}</span>
              <span className={`text-sm font-semibold ${gainLoss >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                {plusSign(gainLoss)}{formatCurrency(gainLoss)}
                {gainLossRate !== null && (
                  <span className="ml-1 text-xs font-normal">
                    （{plusSign(gainLossRate, 1)}{gainLossRate.toFixed(1)}%）
                  </span>
                )}
              </span>
            </div>
          </div>
          {/* 空いた枠には、証券会社を跨いだ預り区分の内訳を出す。
              非課税枠（NISA・iDeCo）にどれだけ乗っているかは一覧では合算できない */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('inv_account_type_breakdown')}</p>
            {accountTypeBreakdown.length === 0 ? (
              <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">{t('inv_account_type_breakdown_empty')}</p>
            ) : (
              <>
                <div className="mt-2 flex h-2.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                  {accountTypeBreakdown.map((row, i) => (
                    <div
                      key={row.accountType}
                      style={{ width: `${row.share}%`, backgroundColor: BREAKDOWN_COLORS[i % BREAKDOWN_COLORS.length] }}
                      title={`${row.label} ${formatCurrency(row.marketValue)}`}
                    />
                  ))}
                </div>
                <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                  {accountTypeBreakdown.map((row, i) => (
                    <li
                      key={row.accountType}
                      className="flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400"
                      title={formatCurrency(row.marketValue)}
                    >
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: BREAKDOWN_COLORS[i % BREAKDOWN_COLORS.length] }}
                      />
                      <span className="truncate">{row.label}</span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">{row.share.toFixed(0)}%</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      )}

      {summary && summary.unpricedCount > 0 && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-400">
          {t('inv_unpriced_warning').replace('{count}', String(summary.unpricedCount))}
        </div>
      )}

      {notice && (
        <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700 whitespace-pre-line dark:bg-emerald-950 dark:border-emerald-800 dark:text-emerald-400">
          {notice}
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 dark:bg-red-950 dark:border-red-800 dark:text-red-400">
          {error}
        </div>
      )}

      {loading ? (
        <div className="mt-4 flex justify-center py-12">
          <p className="text-sm text-gray-400 dark:text-gray-500">{t('common_loading')}</p>
        </div>
      ) : !summary || summary.brokerages.length === 0 ? (
        <div className="mt-4 py-12 text-center">
          <p className="text-sm text-gray-400 dark:text-gray-500">{t('inv_no_brokerages')}</p>
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">{t('inv_import_hint')}</p>
        </div>
      ) : (
        groups.map((group) => (
          <div key={group.key} className="mt-8">
            <h2 className={sectionTitle}>
              {group.name}
              <span className="ml-2 text-sm font-normal text-gray-400 dark:text-gray-500">
                {t('inv_total_market_value')}:{' '}
                {formatCurrency(group.brokerages.reduce((sum, b) => sum + b.marketValue, 0))}
              </span>
            </h2>
            <div className="mt-3 space-y-4">
              {group.brokerages.map((brokerage) => (
                <BrokerageCard
                  key={brokerage.id}
                  brokerage={brokerage}
                  token={accessToken ?? ''}
                  onChanged={fetchSummary}
                  onEditBrokerage={(b) => setBrokerageModal({ open: true, initial: b })}
                  onAddProduct={(brokerageId) => setProductModal({ open: true, brokerageId, initial: null })}
                  onEditProduct={(p) => setProductModal({ open: true, brokerageId: p.brokerageId, initial: p })}
                  onError={(m) => setError(m === '' ? null : m)}
                  onNotice={setNotice}
                />
              ))}
            </div>
          </div>
        ))
      )}

      {unlinkedAccounts.length > 0 && (
        <div className="mt-8">
          <h2 className={sectionTitle}>{t('inv_unlinked_accounts')}</h2>
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{t('inv_unlinked_accounts_hint')}</p>
          <div className={`mt-3 ${cardGrid}`}>
            {unlinkedAccounts.map((account) => (
              <AccountTile
                key={account.id}
                account={account}
                onEdit={setUnlinkedAccount}
                transactionsHref={`/transactions?accountId=${account.id}`}
              />
            ))}
          </div>
        </div>
      )}

      <AccountModal
        open={unlinkedAccount !== null}
        initial={unlinkedAccount}
        allowedTypes={SECURITIES_ONLY_TYPES}
        onClose={() => setUnlinkedAccount(null)}
        onSaved={fetchSummary}
        onArchive={async (account) => {
          if (!accessToken) return
          await accountsApi.update(accessToken, account.id, { isArchived: !account.isArchived })
          fetchSummary()
        }}
        onDelete={async (account) => {
          if (!accessToken) return
          await accountsApi.delete(accessToken, account.id, { permanent: true })
          fetchSummary()
        }}
        token={accessToken ?? ''}
      />

      <BrokerageModal
        open={brokerageModal.open}
        initial={brokerageModal.initial}
        members={members}
        token={accessToken ?? ''}
        onClose={() => setBrokerageModal({ open: false, initial: null })}
        onSaved={fetchSummary}
      />
      <ProductModal
        open={productModal.open}
        brokerageId={productModal.brokerageId}
        initial={productModal.initial}
        accountTypeOptions={accountTypeOptions}
        token={accessToken ?? ''}
        onClose={() => setProductModal({ open: false, brokerageId: '', initial: null })}
        onSaved={fetchSummary}
      />
    </div>
  )
}
